// Reusable styled components for Technical Alerts
// Built using the centralized theme system

import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import theme from './theme';

// Typography Components
export const ThemedTypography = {
  H1: styled(Typography)({
    ...theme.typography.h1,
    color: theme.colors.text.primary
  }),
  
  H2: styled(Typography)({
    ...theme.typography.h2,
    color: theme.colors.text.primary
  }),
  
  H3: styled(Typography)({
    ...theme.typography.h3,
    color: theme.colors.text.primary
  }),
  
  H4: styled(Typography)({
    ...theme.typography.h4,
    color: theme.colors.text.secondary
  }),
  
  H5: styled(Typography)({
    ...theme.typography.h5,
    color: theme.colors.text.secondary
  }),
  
  Body1: styled(Typography)({
    ...theme.typography.body1,
    color: theme.colors.text.secondary,
    letterSpacing: '-0.01em'
  }),
  
  Body2: styled(Typography)({
    ...theme.typography.body2,
    color: theme.colors.text.secondary,
    letterSpacing: '-0.01em'
  }),
  
  Body3: styled(Typography)({
    ...theme.typography.body3,
    color: theme.colors.text.secondary,
    letterSpacing: '-0.01em'
  }),
  
  Caption: styled(Typography)({
    ...theme.typography.caption,
    color: theme.colors.text.light
  }),
  
  Micro: styled(Typography)({
    ...theme.typography.micro,
    color: theme.colors.text.light
  }),
  
  Tiny: styled(Typography)({
    ...theme.typography.tiny,
    color: theme.colors.text.light
  }),
  
  Smallest: styled(Typography)({
    ...theme.typography.smallest,
    color: theme.colors.text.light,
    textTransform: 'uppercase',
    letterSpacing: '0.06em'
  }),
  
  // Specialized variants
  TableCell: styled(Typography)({
    ...theme.typography.body3,
    color: theme.colors.text.secondary,
    letterSpacing: '-0.01em'
  }),
  
  Clickable: styled(Typography)({
    ...theme.typography.body2,
    color: theme.colors.text.secondary,
    textDecoration: 'none',
    cursor: 'pointer',
    letterSpacing: '-0.01em',
    transition: theme.transitions.normal,
    '&:hover': {
      color: theme.colors.text.primary,
      textDecoration: 'underline'
    }
  })
};

// Container Components
export const ThemedBox = {
  Card: styled(Box)({
    ...theme.commonStyles.card
  }),
  
  CardSelected: styled(Box)({
    ...theme.commonStyles.card,
    backgroundColor: theme.colors.background.light,
    borderColor: theme.colors.border.focus,
    boxShadow: theme.shadows.focus
  }),
  
  CardSuccess: styled(Box)({
    ...theme.commonStyles.card,
    backgroundColor: theme.colors.successBackground,
    borderColor: theme.colors.success
  }),
  
  FlexCenter: styled(Box)({
    ...theme.commonStyles.flexCenter
  }),
  
  FlexBetween: styled(Box)({
    ...theme.commonStyles.flexBetween
  }),
  
  FlexColumn: styled(Box)({
    ...theme.commonStyles.flexColumn
  }),
  
  // Specialized containers
  PopoverContainer: styled(Box)({
    padding: theme.spacing.xl,
    minWidth: '160px'
  }),
  
  DropdownContainer: styled(Box)({
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: theme.spacing.md,
    backgroundColor: theme.colors.background.white,
    borderRadius: theme.borderRadius.xl,
    boxShadow: theme.shadows.lg,
    overflow: 'hidden',
    minWidth: '320px',
    zIndex: theme.zIndex.dropdown
  }),
  
  FilterSection: styled(Box)({
    padding: theme.spacing.xl,
    borderBottom: `1px solid ${theme.colors.border.light}`
  })
};

// Button Components
export const ThemedButton = {
  Primary: styled(Button)({
    ...theme.typography.caption,
    backgroundColor: theme.colors.alert,
    color: theme.colors.background.white,
    textTransform: 'none',
    borderRadius: theme.borderRadius.md,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    border: 'none',
    boxShadow: 'none',
    transition: theme.transitions.normal,
    '&:hover': {
      backgroundColor: theme.colors.alertHover,
      boxShadow: 'none'
    },
    '&:active': {
      transform: 'scale(0.98)',
      transition: theme.transitions.fast
    }
  }),
  
  Secondary: styled(Button)({
    ...theme.typography.caption,
    backgroundColor: 'transparent',
    color: theme.colors.text.secondary,
    textTransform: 'none',
    borderRadius: theme.borderRadius.md,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    border: `1px solid ${theme.colors.border.medium}`,
    boxShadow: 'none',
    transition: theme.transitions.normal,
    '&:hover': {
      backgroundColor: theme.colors.background.hover,
      borderColor: theme.colors.border.focus,
      boxShadow: 'none'
    },
    '&:active': {
      transform: 'scale(0.98)',
      transition: theme.transitions.fast
    }
  }),
  
  Danger: styled(Button)({
    ...theme.typography.caption,
    backgroundColor: theme.colors.alert,
    color: theme.colors.background.white,
    textTransform: 'none',
    borderRadius: theme.borderRadius.sm,
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    border: 'none',
    boxShadow: 'none',
    transition: theme.transitions.normal,
    '&:hover': {
      backgroundColor: theme.colors.alertHover,
      boxShadow: 'none'
    },
    '&:disabled': {
      backgroundColor: theme.colors.text.disabled,
      color: '#666666'
    }
  }),
  
  OutlineDanger: styled(Button)({
    ...theme.typography.tiny,
    backgroundColor: 'transparent',
    color: theme.colors.alert,
    textTransform: 'none',
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm,
    border: `1px solid ${theme.colors.alert}`,
    boxShadow: 'none',
    transition: theme.transitions.normal,
    '&:hover': {
      backgroundColor: theme.colors.alertBackground,
      borderColor: theme.colors.alertHover,
      boxShadow: 'none'
    },
    '&:active': {
      transform: 'scale(0.98)',
      transition: theme.transitions.fast
    }
  })
};

// Common style functions for dynamic styling
export const getPopoverStyles = (type = 'default') => ({
  zIndex: theme.zIndex.popover,
  '& .MuiPaper-root': { 
    overflow: 'visible'
  }
});

export const getDropdownItemStyles = (isActive = false) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing.xl,
  cursor: 'pointer',
  ...theme.typography.body2,
  color: isActive ? theme.colors.primaryHover : theme.colors.text.primary,
  backgroundColor: isActive ? theme.colors.primaryBackground : 'transparent',
  '&:hover': {
    backgroundColor: isActive 
      ? 'rgba(0, 122, 255, 0.12)' 
      : theme.colors.background.hover
  }
});

export const getFilterPillStyles = (isActive = false) => ({
  ...theme.typography.caption,
  padding: `${theme.spacing.xs} ${theme.spacing.md}`,
  backgroundColor: isActive ? theme.colors.primaryBackground : theme.colors.background.hover,
  color: isActive ? theme.colors.primary : theme.colors.text.secondary,
  border: isActive ? `1px solid ${theme.colors.primaryBorder}` : 'none',
  borderRadius: theme.borderRadius.lg,
  cursor: 'pointer',
  transition: theme.transitions.normal,
  '&:hover': {
    backgroundColor: isActive 
      ? 'rgba(0, 122, 255, 0.12)' 
      : 'rgba(0, 0, 0, 0.06)'
  }
});

// Table-specific styles
export const tableStyles = {
  cell: {
    ...theme.typography.body3,
    color: theme.colors.text.secondary,
    letterSpacing: '-0.01em'
  },
  headerCell: {
    ...theme.typography.body2,
    color: theme.colors.text.primary,
    fontWeight: 600
  }
};

// Utility function to create consistent table cell content
export const createTableCell = (content) => (
  <span style={tableStyles.cell}>
    {content || '-'}
  </span>
);

const components = {
  ThemedTypography,
  ThemedBox,
  ThemedButton,
  getPopoverStyles,
  getDropdownItemStyles,
  getFilterPillStyles,
  tableStyles,
  createTableCell
};

export default components;